/**
 * Smart Image Preloader - Preloads images before they enter the viewport
 */

class SmartImagePreloader {
  constructor() {
    this.preloadedImages = new Set();
    this.preloadQueue = [];
    this.isProcessing = false;
    this.observer = null;
    this.preloadDistance = 1000; // Preload images 1000px before they're visible
  }

  /**
   * Initialize the preloader with posts
   * @param {Array} posts - Array of post objects
   */
  initialize(posts) {
    this.clearCache();
    this.extractImageUrls(posts);
    this.startPreloading();
  }

  /**
   * Extract all image URLs from posts
   * @param {Array} posts - Array of post objects
   */
  extractImageUrls(posts) {
    posts.forEach((post, index) => {
      let imageUrl = null;
      
      if (post.post_images && post.post_images.length > 0) {
        imageUrl = post.post_images[0].url;
      } else if (post.image_url) {
        imageUrl = post.image_url;
      }

      if (imageUrl && !this.preloadedImages.has(imageUrl)) {
        this.preloadQueue.push({
          url: imageUrl,
          postIndex: index,
          priority: index < 5 ? 'high' : 'medium' // First 5 posts are high priority
        });
      }
    });

    console.log(`📸 Smart preloader: ${this.preloadQueue.length} images queued for preloading`);
  }

  /**
   * Start preloading images with priority
   */
  async startPreloading() {
    if (this.isProcessing || this.preloadQueue.length === 0) return;

    this.isProcessing = true;
    console.log('🚀 Starting smart image preloading...');

    // Sort by priority (high priority first)
    this.preloadQueue.sort((a, b) => {
      if (a.priority === 'high' && b.priority !== 'high') return -1;
      if (a.priority !== 'high' && b.priority === 'high') return 1;
      return a.postIndex - b.postIndex;
    });

    // Preload high priority images immediately (first 5 posts)
    const highPriorityImages = this.preloadQueue.filter(item => item.priority === 'high');
    await this.preloadBatch(highPriorityImages, 3); // 3 concurrent loads

    // Preload remaining images in background
    const remainingImages = this.preloadQueue.filter(item => item.priority !== 'high');
    this.preloadBatch(remainingImages, 2); // 2 concurrent loads in background

    this.isProcessing = false;
  }

  /**
   * Preload a batch of images
   * @param {Array} images - Array of image objects to preload
   * @param {number} concurrency - Number of concurrent preloads
   */
  async preloadBatch(images, concurrency = 2) {
    const chunks = this.chunkArray(images, concurrency);

    for (const chunk of chunks) {
      const promises = chunk.map(item => this.preloadSingleImage(item.url));
      
      try {
        await Promise.allSettled(promises);
        // Small delay between chunks to not overwhelm the browser
        await new Promise(resolve => setTimeout(resolve, 50));
      } catch (error) {
        console.warn('Error preloading image batch:', error);
      }
    }
  }

  /**
   * Preload a single image
   * @param {string} url - Image URL to preload
   */
  preloadSingleImage(url) {
    return new Promise((resolve, reject) => {
      if (this.preloadedImages.has(url)) {
        resolve(url);
        return;
      }

      const img = new Image();
      
      img.onload = () => {
        this.preloadedImages.add(url);
        console.log(`✅ Preloaded: ${url}`);
        resolve(url);
      };
      
      img.onerror = () => {
        console.warn(`❌ Failed to preload: ${url}`);
        reject(new Error(`Failed to preload: ${url}`));
      };

      img.src = url;
    });
  }

  /**
   * Check if an image is preloaded
   * @param {string} url - Image URL to check
   * @returns {boolean} - True if image is preloaded
   */
  isPreloaded(url) {
    return this.preloadedImages.has(url);
  }

  /**
   * Get preload status for a post
   * @param {Object} post - Post object
   * @returns {Object} - Preload status
   */
  getPostPreloadStatus(post) {
    let imageUrl = null;
    
    if (post.post_images && post.post_images.length > 0) {
      imageUrl = post.post_images[0].url;
    } else if (post.image_url) {
      imageUrl = post.image_url;
    }

    return {
      hasImage: !!imageUrl,
      isPreloaded: imageUrl ? this.isPreloaded(imageUrl) : false,
      url: imageUrl
    };
  }

  /**
   * Add new posts to preload queue
   * @param {Array} newPosts - New posts to preload
   */
  addPosts(newPosts) {
    this.extractImageUrls(newPosts);
    if (!this.isProcessing) {
      this.startPreloading();
    }
  }

  /**
   * Clear the preload cache
   */
  clearCache() {
    this.preloadedImages.clear();
    this.preloadQueue = [];
    this.isProcessing = false;
  }

  /**
   * Split array into chunks
   * @param {Array} array - Array to chunk
   * @param {number} size - Chunk size
   * @returns {Array} - Array of chunks
   */
  chunkArray(array, size) {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  /**
   * Get preload statistics
   * @returns {Object} - Preload statistics
   */
  getStats() {
    return {
      totalQueued: this.preloadQueue.length,
      totalPreloaded: this.preloadedImages.size,
      isProcessing: this.isProcessing
    };
  }
}

// Create singleton instance
const smartImagePreloader = new SmartImagePreloader();

export default smartImagePreloader;
